import React, { useMemo } from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  BUCKET_META,
  BUCKET_META_STYLES,
  getStakeholderLaneAppearance,
  LANE_STATUS_META,
  STAKEHOLDER_LANES,
} from "./config/stakeholderLaneConfig";
import AdvisoryPartnersLanePage from "./lanes/advisory-partners/AdvisoryPartnersLanePage";
import DadPartnersLanePage from "./lanes/dad-partners/DadPartnersLanePage";
import GuidePartnersLanePage from "./lanes/guide-partners/GuidePartnersLanePage";
import OperationsLanePage from "./lanes/operations/OperationsLanePage";
import ProspectsLanePage from "./lanes/prospects/ProspectsLanePage";
import SupportPartnersLanePage from "./lanes/support-partners/SupportPartnersLanePage";
import SuperFansLanePage from "./lanes/super-fans/SuperFansLanePage";

function DetailItem({ item, onOpen }) {
  const appearance = getStakeholderLaneAppearance(item.lane);
  const disablePersonRouteActions = !!item.disable_person_route_actions;

  return (
    <button
      className={`w-full rounded-2xl border border-slate-200/80 bg-white/90 p-4 text-left shadow-sm transition hover:shadow-md dark:border-white/10 dark:bg-white/[0.04] ${appearance.cardHoverClass} ${disablePersonRouteActions ? "cursor-not-allowed opacity-70" : ""}`}
      disabled={disablePersonRouteActions}
      onClick={() => onOpen(item.id)}
      type="button"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-950 dark:text-white">{item.title}</p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{item.reason}</p>
        </div>
        {item.is_kevin_flagged ? <Badge>{item.urgency_label || "Direct"}</Badge> : null}
      </div>
      {item.next_move ? (
        <p className={`mt-3 text-xs font-medium uppercase tracking-[0.16em] ${appearance.accentTextClass}`}>
          Next: {item.next_move}
        </p>
      ) : null}
      {item.last_meaningful_movement ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.last_meaningful_movement}</p>
      ) : null}
    </button>
  );
}

export default function StakeholderLanePage({ board, onOpenItem }) {
  const navigate = useNavigate();
  const { laneId } = useParams();

  const lane = useMemo(() => board.find((entry) => entry.id === laneId) || null, [board, laneId]);
  const laneConfig = useMemo(
    () => STAKEHOLDER_LANES.find((entry) => entry.id === laneId) || null,
    [laneId]
  );

  if (!lane || !laneConfig) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.12),_transparent_24%),linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)] pb-10 dark:bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.09),_transparent_24%),linear-gradient(180deg,#05080d_0%,#0a1118_100%)]">
        <div className="mx-auto max-w-[1200px] px-4 py-6 sm:px-6 lg:px-8">
          <div className="rounded-[30px] border border-slate-200/80 bg-white/85 p-6 shadow-[0_35px_80px_-38px_rgba(15,23,42,0.45)] backdrop-blur dark:border-white/10 dark:bg-[#081018]/90">
            <p className="text-sm text-slate-600 dark:text-slate-300">That community lane could not be found.</p>
            <Button className="mt-4" onClick={() => navigate("/community")} variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Community Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const statusMeta = LANE_STATUS_META[lane.status];

  if (laneId === "prospects") {
    return <ProspectsLanePage lane={lane} laneConfig={laneConfig} onOpen={onOpenItem} />;
  }

  if (laneId === "dad-partners") {
    return <DadPartnersLanePage lane={lane} laneConfig={laneConfig} onOpen={onOpenItem} />;
  }

  if (laneId === "guide-partners") {
    return <GuidePartnersLanePage lane={lane} laneConfig={laneConfig} onOpen={onOpenItem} />;
  }

  if (laneId === "operations") {
    return <OperationsLanePage lane={lane} laneConfig={laneConfig} onOpen={onOpenItem} />;
  }

  if (laneId === "support-partners") {
    return <SupportPartnersLanePage lane={lane} laneConfig={laneConfig} onOpen={onOpenItem} />;
  }

  if (laneId === "super-fans") {
    return <SuperFansLanePage lane={lane} laneConfig={laneConfig} onOpen={onOpenItem} />;
  }

  if (laneId === "advisory-partners") {
    return <AdvisoryPartnersLanePage lane={lane} laneConfig={laneConfig} onOpen={onOpenItem} />;
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.12),_transparent_24%),linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)] pb-10 dark:bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.09),_transparent_24%),linear-gradient(180deg,#05080d_0%,#0a1118_100%)]">
      <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-[30px] border border-slate-200/80 bg-white/85 p-6 shadow-[0_35px_80px_-38px_rgba(15,23,42,0.45)] backdrop-blur dark:border-white/10 dark:bg-[#081018]/90">
          <div className="flex flex-col gap-5 border-b border-slate-200/80 pb-6 dark:border-white/10 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-4xl">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700 dark:text-aqua">
                Lane Detail
              </p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950 dark:text-white">
                {lane.title}
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{lane.promise}</p>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                {laneConfig.charter}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Badge className={`border ${statusMeta.className}`}>{statusMeta.label}</Badge>
              <Button onClick={() => navigate("/community")} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Community Home
              </Button>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl border border-rose-200/70 bg-rose-50/80 p-5 dark:border-rose-400/20 dark:bg-rose-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-700 dark:text-rose-100">
                Needs Now
              </p>
              <p className="mt-2 text-3xl font-semibold text-rose-950 dark:text-white">
                {lane.counts["needs-now"]}
              </p>
            </div>
            <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/80 p-5 dark:border-emerald-400/20 dark:bg-emerald-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-100">
                Moving
              </p>
              <p className="mt-2 text-3xl font-semibold text-emerald-950 dark:text-white">
                {lane.counts.moving}
              </p>
            </div>
            <div className="rounded-2xl border border-amber-200/70 bg-amber-50/80 p-5 dark:border-amber-400/20 dark:bg-amber-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-100">
                Watch
              </p>
              <p className="mt-2 text-3xl font-semibold text-amber-950 dark:text-white">
                {lane.counts.watch}
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-6">
            {Object.entries(lane.items).map(([bucket, items]) => {
              const meta = BUCKET_META[bucket];
              const styles = BUCKET_META_STYLES[bucket];

              return (
                <section
                  className={`rounded-[26px] border bg-slate-50/85 p-5 dark:bg-white/[0.03] ${styles.borderClass}`}
                  key={bucket}
                >
                  <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 pb-4 dark:border-white/10">
                    <div>
                      <h2 className={`text-lg font-semibold ${styles.headingClass}`}>{meta.label}</h2>
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{meta.empty}</p>
                    </div>
                    <Badge variant="outline">{items.length}</Badge>
                  </div>

                  <div className="mt-4 space-y-3">
                    {items.length ? (
                      items.map((item) => (
                        <DetailItem item={item} key={item.id} onOpen={onOpenItem} />
                      ))
                    ) : (
                      <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                        {meta.empty}
                      </p>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
